<template>
  <div class="resource-activity-log-view">
    <div class="view-header">
      <button class="back-btn" @click="$emit('back')" aria-label="Back">
        <i class="tim-icons icon-minimal-left"></i>
      </button>
      <h5 class="view-title mb-0">Activity log</h5>
    </div>

    <div v-if="loading" class="view-body text-center py-5">
      <i class="el-icon-loading" style="font-size: 24px;"></i>
    </div>

    <div v-else-if="entries.length === 0" class="view-body">
      <div class="empty-state">
        <i class="tim-icons icon-bullet-list-67 mb-2 empty-icon"></i>
        <p class="empty-text mb-0">No activity has been recorded yet.</p>
      </div>
    </div>

    <div v-else class="view-body">
      <div class="log-list">
        <div v-for="entry in entries" :key="entry.id" class="log-item">
          <div class="log-row">
            <div class="log-left">
              <div class="log-event text-white">
                {{ entry.description }}
                <span v-if="hasBothValues(entry) && isTimeValue(entry.new_value)">
                  from {{ formatTime(entry.old_value) }} to {{ formatTime(entry.new_value) }}
                </span>
                <span v-else-if="hasBothValues(entry) && isDateValue(entry.new_value)">
                  from {{ formatDate(entry.old_value) }} to {{ formatDate(entry.new_value) }}
                </span>
                <template v-else>
                  <span v-if="entry.old_value" class="log-detail"> from {{ entry.old_value }}</span>
                  <span v-if="entry.new_value" class="log-detail"> to {{ entry.new_value }}</span>
                </template>
              </div>
              <div class="log-actor">
                {{ entry.company_user_id ? entry.company_user_name : 'System' }}
              </div>
            </div>
            <div class="log-date">
              {{ formatDateTime(entry.timestamp) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'ResourceActivityLogView',

  props: {
    entries: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    dateFormat: { type: String, default: 'MM/DD/YYYY' },
    timeFormat: { type: String, default: 'hh:mm A' }
  },

  methods: {
    hasBothValues(entry) {
      return !!(entry.old_value && entry.new_value);
    },

    isTimeValue(str) {
      return /^\d{2}:\d{2}(:\d{2})?$/.test(str);
    },

    isDateValue(str) {
      return moment(str, 'YYYY-MM-DD', true).isValid()
        || (typeof str === 'string' && str.includes('T') && moment(str).isValid());
    },

    formatTime(time) {
      if (!time) return '';
      const t = time.includes && time.includes('T') ? time.split('T')[1] : time;
      const parts = String(t).split(':');
      if (parts.length < 2) return time;
      const h = parseInt(parts[0], 10);
      if (this.timeFormat === 'HH:mm') return `${parts[0]}:${parts[1]}`;
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      return `${h12}:${parts[1]} ${ampm}`;
    },

    formatDate(d) {
      if (!d) return '';
      const clean = typeof d === 'string' && d.length > 10 ? d.split('T')[0] : d;
      return moment(clean, 'YYYY-MM-DD').format(this.dateFormat);
    },

    formatDateTime(d) {
      if (!d) return '';
      const fmt = this.timeFormat === 'HH:mm' ? 'HH:mm' : 'h:mm A';
      return moment(d).format(this.dateFormat + ', ' + fmt);
    }
  }
};
</script>

<style lang="scss" scoped>
.resource-activity-log-view {
  display: flex;
  flex-direction: column;
  margin: -16px -24px;
}

.view-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(125, 125, 125, 0.18);
}

.back-btn {
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(125, 125, 125, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
}

.view-title {
  font-size: 14px;
  font-weight: 600;
  color: #fff;
}

.view-body {
  padding: 16px 24px;
  overflow-y: auto;
}

.empty-state {
  text-align: center;
  padding: 40px 0;
}

.empty-icon {
  font-size: 24px;
  display: block;
  color: rgba(125, 125, 125, 0.7);
}

.empty-text {
  color: rgba(125, 125, 125, 0.9);
  font-size: 13px;
  margin-top: 8px;
}

.log-list {
  display: flex;
  flex-direction: column;
}

.log-item {
  padding: 12px 0;
  border-bottom: 1px solid rgba(125, 125, 125, 0.15);

  &:last-child { border-bottom: none; }
}

.log-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.log-left {
  flex: 1;
  min-width: 0;
}

.log-event {
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 2px;
}

.log-detail {
  color: rgba(255, 255, 255, 0.55);
}

.log-actor {
  font-size: 11px;
  color: rgba(125, 125, 125, 0.9);
  font-weight: 500;
}

.log-date {
  text-align: right;
  white-space: nowrap;
  flex-shrink: 0;
  font-size: 11px;
  color: rgba(125, 125, 125, 0.9);
  line-height: 1.4;
}
</style>

<style lang="scss">
.white-content {
  .resource-activity-log-view .view-header {
    border-bottom-color: #e3e3e3;
  }
  .resource-activity-log-view .back-btn {
    background: #f5f6fa;
    border-color: #e3e3e3;
    color: #525f7f;
    &:hover { background: #e9ecef; color: #344675; }
  }
  .resource-activity-log-view .view-title { color: #344675; }
  .resource-activity-log-view .log-event { color: #344675; }
  .resource-activity-log-view .log-detail { color: #6b7383; }
  .resource-activity-log-view .log-item { border-bottom-color: #e3e3e3; }
  .resource-activity-log-view .empty-icon { color: #9A9A9A; }
}
</style>
